// Deterministic video thumbnails for the timeline labels / hover previews.
//
// A <video preload="metadata"> with a #t media fragment is NOT guaranteed to
// paint — the browser may never decode the requested frame (especially when
// seeking past 0), leaving an empty box. Here the frame is decoded once,
// drawn to a canvas and cached as a data URL, so the preview renders the same
// regardless of where the clip sits on the timeline or which scene it's in.

import { credentialsFor } from "../../../../../common/utils";

const _thumbCache = new Map(); // `${url}|${seekCentisec}` -> Promise<string|null>

export function getVideoThumbnail(url, seekSec = 0.1) {
  if (!url) return Promise.resolve(null);
  const key = `${url}|${Math.round((seekSec || 0) * 100)}`;
  if (_thumbCache.has(key)) return _thumbCache.get(key);

  const promise = new Promise((resolve) => {
    let video;
    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (video) {
        video.removeAttribute("src");
        try {
          video.load();
        } catch {
          /* ignore */
        }
      }
      resolve(result);
    };
    const timer = setTimeout(() => finish(null), 8000);
    try {
      video = document.createElement("video");
      // Capture needs a non-tainted canvas. If the media host doesn't allow
      // CORS, the load errors out and we resolve null — the caller then falls
      // back to a live, fully-preloaded <video> tag (view-only, no capture).
      video.crossOrigin = credentialsFor(url).credentials ? "use-credentials" : "anonymous";
      video.muted = true;
      video.playsInline = true;
      video.preload = "auto";
      video.addEventListener("error", () => finish(null));
      video.addEventListener("loadedmetadata", () => {
        const d = video.duration;
        const target =
          Number.isFinite(d) && d > 0 ? Math.min(seekSec, Math.max(0, d - 0.05)) : seekSec;
        try {
          video.currentTime = Math.max(0, target);
        } catch {
          finish(null);
        }
      });
      video.addEventListener("seeked", () => {
        try {
          const w = video.videoWidth || 0;
          const h = video.videoHeight || 0;
          if (!w || !h) return finish(null);
          const canvas = document.createElement("canvas");
          const outW = 320; // serves both the 19px label and the 140px hover preview
          canvas.width = outW;
          canvas.height = Math.max(1, Math.round((outW * h) / w));
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          finish(canvas.toDataURL("image/jpeg", 0.72));
        } catch {
          finish(null); // tainted canvas or draw failure
        }
      });
      video.src = url;
    } catch {
      finish(null);
    }
  });
  _thumbCache.set(key, promise);
  return promise;
}
